import type { RouteObject } from 'react-router';

import { lazy } from 'react';
import { Navigate } from 'react-router';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { HqGuard, AuthGuard, AdminGuard, PermissionGuard } from 'src/auth/guard';

import { paths } from '../paths';
import { RoleScopeRedirect } from './role-scope-redirect';
import { LoadingScreenSuspenseOutlet } from '../components/route-outlet';

// ================================================================================================
// 超管（HQ）后台 — 菜单路由
// ================================================================================================

const PageHqOverview = lazy(() => import('src/pages/hq/overview/index'));
const PageHqCities = lazy(() => import('src/pages/hq/cities/index'));

// ================================================================================================
// 租户管理
// ================================================================================================

/** 租户列表 */
const PageHqTenantList = lazy(() => import('src/pages/hq/tenants/index'));

/** 入驻申请审核 */
const PageHqEnterpriseVerification = lazy(
  () => import('src/pages/hq/enterprise-verification/index')
);

// ================================================================================================
// 用户管理
// ================================================================================================

/** 用户与认证 */
const PageHqAppUsers = lazy(() => import('src/pages/hq/app-users/index'));

/** 注销审核 */
// const PageHqAppUserDeletions = lazy(() => import('src/pages/hq/app-user-deletions/index'));

// ================================================================================================
// 财务中心
// ================================================================================================

/** 财务概览 */
const PageHqFinanceOverview = lazy(() => import('src/pages/hq/finance/overview/index'));
/** 支付管理 */
const PageHqFinancePayments = lazy(() => import('src/pages/hq/finance/payments/index'));
/** 结算记录 */
const PageHqFinanceClearingRecords = lazy(
  () => import('src/pages/hq/finance/clearing-records/index')
);
/** 结算管理 */
const PageHqFinanceSettlements = lazy(() => import('src/pages/hq/finance/settlements/index'));
/** 提现管理 */
const PageHqFinanceWithdrawals = lazy(() => import('src/pages/hq/finance/withdrawals/index'));
/** 退款管理 */
const PageHqFinanceRefunds = lazy(() => import('src/pages/hq/finance/refunds/index'));
/** 结算规则 */
const PageHqFinanceClearingRules = lazy(() => import('src/pages/hq/finance/clearing-rules/index'));
/** 分账参与方 */
const PageHqFinanceParticipants = lazy(() => import('src/pages/hq/finance/participants/index'));
/** 对账管理 */
const PageHqFinanceReconciliation = lazy(() => import('src/pages/hq/finance/reconciliation/index'));

/** 订单列表 */
const PageHqOrderOverview = lazy(() => import('src/pages/hq/orders/overview/index'));
const PageHqOrderList = lazy(() => import('src/pages/hq/orders/index'));
const PageHqOrderOverdue = lazy(() => import('src/pages/hq/orders/overdue/index'));

// ================================================================================================
// 系统管理
// ================================================================================================

/** 管理员用户 */
const PageHqAdminUsers = lazy(() => import('src/pages/hq/admin-users/index'));

/** 角色管理 */
const PageHqRoles = lazy(() => import('src/pages/hq/roles/index'));
/** 权限管理 */
const PageHqPermissions = lazy(() => import('src/pages/hq/permissions/index'));
/** 审计日志 */
const PageHqAuditLogs = lazy(() => import('src/pages/hq/audit-logs/index'));
/** 监控管理 */
const PageHqMonitoring = lazy(() => import('src/pages/hq/monitoring/index'));
/** 平台系统设置 */
const PageHqPlatformSetting = lazy(() => import('src/pages/hq/platform-setting/index'));
/** 系统配置（JSON） */
const PageHqConfigurations = lazy(() => import('src/pages/hq/configurations/index'));
/** 菜单管理 */
const PageHqMenus = lazy(() => import('src/pages/hq/menus/index'));

/** 即将上线 */
const PageComingSoon = lazy(() => import('src/pages/coming-soon/index'));
// ================================================================================================

const hqLayout = () => (
  <DashboardLayout navScope="hq">
    <LoadingScreenSuspenseOutlet />
  </DashboardLayout>
);

const wrapWithGuard = (el: React.ReactNode) =>
  CONFIG.auth.skip ? (
    el
  ) : (
    <AuthGuard>
      <AdminGuard>
        <HqGuard>
          <PermissionGuard scope="hq">{el}</PermissionGuard>
        </HqGuard>
      </AdminGuard>
    </AuthGuard>
  );

// ================================================================================================

export const hqRoutes: RouteObject[] = [
  {
    path: 'hq',
    element: wrapWithGuard(hqLayout()),
    children: [
      { index: true, element: <Navigate to={paths.hq.overview} replace /> },
      { path: 'overview', element: <PageHqOverview /> },
      { path: 'cities', element: <PageHqCities /> },
      {
        /** 租户管理 */
        path: 'tenant',
        children: [
          { index: true, element: <Navigate to={paths.hq.tenant.list} replace /> },
          { path: 'list', element: <PageHqTenantList /> }, // 租户列表
          { path: 'enterprise-verification', element: <PageHqEnterpriseVerification /> }, // 入驻申请审核
        ],
      },
      {
        /** 用户管理 */
        path: 'app-user', // 用户与认证
        children: [
          { index: true, element: <Navigate to={paths.hq.appUser.list} replace /> },
          { path: 'list', element: <PageHqAppUsers /> }, // 用户列表
          { path: 'deletions', element: <PageComingSoon /> }, // 注销审核
        ],
      },
      {
        /** 财务中心 */
        path: 'finance',
        children: [
          { index: true, element: <Navigate to={paths.hq.finance.overview} replace /> },
          { path: 'overview', element: <PageHqFinanceOverview /> },
          { path: 'payments', element: <PageHqFinancePayments /> },
          { path: 'clearing-records', element: <PageHqFinanceClearingRecords /> },
          { path: 'settlements', element: <PageHqFinanceSettlements /> },
          { path: 'withdrawals', element: <PageHqFinanceWithdrawals /> },
          { path: 'refunds', element: <PageHqFinanceRefunds /> },
          { path: 'clearing-rules', element: <PageHqFinanceClearingRules /> },
          { path: 'participants', element: <PageHqFinanceParticipants /> },
          { path: 'reconciliation', element: <PageHqFinanceReconciliation /> },
        ],
      },
      {
        /** 订单管理 */
        path: 'order',
        children: [
          { index: true, element: <Navigate to={paths.hq.order.overview} replace /> },
          { path: 'overview', element: <PageHqOrderOverview /> }, // 数据看板
          { path: 'list', element: <PageHqOrderList /> }, // 订单列表
          { path: 'overdue', element: <PageHqOrderOverdue /> }, // 逾期管理
        ],
      },
      {
        /** 账号管理 */
        path: 'account',
        children: [
          { index: true, element: <Navigate to={paths.hq.account.adminUsers} replace /> },
          { path: 'admin-users', element: <PageHqAdminUsers /> },
          { path: 'roles', element: <PageHqRoles /> },
          { path: 'permissions', element: <PageHqPermissions /> },
        ],
      },
      {
        /** 系统管理 */
        path: 'system',
        children: [
          { index: true, element: <Navigate to={paths.hq.system.overview} replace /> },
          { path: 'overview', element: <PageComingSoon /> },
          { path: 'audit-logs', element: <PageHqAuditLogs /> },
          { path: 'monitoring', element: <PageHqMonitoring /> },
          { path: 'menus', element: <PageHqMenus /> },
          { path: 'settings', element: <PageHqPlatformSetting /> },
          { path: 'configurations', element: <PageHqConfigurations /> },
        ],
      },
      { path: '*', element: <RoleScopeRedirect /> },
    ],
  },
];
